import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, InputLabel, Switch, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { useMaterialUIController } from "context";
import {
    CORES_PLANILHA, ESTILO_PADRAO, FONTES_PLANILHA, TAMANHOS_FONTE, clarear, nomeDaCor, normalizarEstilo, textoSobre,
} from "./estiloPlanilha";
import { ESCURO } from "assets/coresTema";

// Linhas da prévia: só para mostrar a cara da planilha
const EXEMPLO = [
    ["Ana Souza", "01/10/2026", "R$ 1.850,00"],
    ["Bruno Lima", "02/10/2026", "R$ 2.340,50"],
    ["Carla Dias", "02/10/2026", "R$ 980,00"],
    ["Diego Alves", "03/10/2026", "R$ 1.420,00"],
];

/** Escolha da aparência da planilha exportada, com prévia ao vivo. Salvar guarda para o usuário neste navegador. */
export default function EstiloPlanilhaDialog({ open, estilo, onClose, onSalvar }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [atual, setAtual] = useState(normalizarEstilo(estilo));

    useEffect(() => {
        if (open) setAtual(normalizarEstilo(estilo));
    }, [open, estilo]);

    const textoPrincipal = darkMode ? "#FFFFFF" : "#344767";
    const textoSecundario = darkMode ? "rgba(255, 255, 255, 0.8)" : "#6c757d";
    const labelStyle = { pb: 0.5 }; // aparência do rótulo vem do tema
    const alterar = (campo, valor) => setAtual(a => normalizarEstilo({ ...a, [campo]: valor }));
    const corPersonalizada = !CORES_PLANILHA.some(c => c.cor === atual.cor);

    const fonteCss = `"${atual.fonte}", Calibri, Arial, sans-serif`;
    const px = (pt) => `${Math.round(pt * 1.25)}px`; // prévia em escala aproximada da tela
    const celula = { padding: "4px 10px", borderBottom: "1px solid #D9D9D9", whiteSpace: "nowrap" };

    const opcaoSwitch = (campo, titulo, ajuda) => (
        <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} py={0.5}>
            <MDBox>
                <MDTypography variant="button" fontWeight="medium" display="block" sx={{ color: textoPrincipal }}>{titulo}</MDTypography>
                <MDTypography variant="caption" display="block" sx={{ color: textoSecundario }}>{ajuda}</MDTypography>
            </MDBox>
            <Switch checked={atual[campo]} onChange={(e) => alterar(campo, e.target.checked)} inputProps={{ "aria-label": titulo }} />
        </MDBox>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: darkMode ? `linear-gradient(145deg, ${ESCURO.campo} 0%, ${ESCURO.painel} 100%)` : "#ffffff",
                    color: darkMode ? "#FFFFFF" : "inherit",
                    borderRadius: "12px",
                    border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e0e0e0",
                },
            }}
        >
            <DialogTitle sx={{ pb: 0.5 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: textoPrincipal }}>Aparência da planilha</MDTypography>
                <MDTypography variant="caption" display="block" sx={{ color: textoSecundario }}>
                    Vale para todas as planilhas que você baixar dos relatórios. Fica salva para você neste navegador.
                </MDTypography>
            </DialogTitle>

            <DialogContent sx={{ pt: "12px !important" }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={5}>
                        <InputLabel sx={labelStyle}>Cor</InputLabel>
                        <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
                            {CORES_PLANILHA.map(c => {
                                const ativa = atual.cor === c.cor;
                                return (
                                    <Tooltip key={c.id} title={c.nome}>
                                        <MDBox
                                            component="button"
                                            type="button"
                                            aria-label={`Cor ${c.nome}`}
                                            aria-pressed={ativa}
                                            onClick={() => alterar("cor", c.cor)}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "8px",
                                                cursor: "pointer",
                                                backgroundColor: c.cor,
                                                border: "none",
                                                outline: ativa ? `2px solid ${darkMode ? "#FFFFFF" : "#1A73E8"}` : "none",
                                                outlineOffset: "2px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {ativa && <Icon sx={{ color: `${textoSobre(c.cor)} !important`, fontSize: "18px !important" }}>check</Icon>}
                                        </MDBox>
                                    </Tooltip>
                                );
                            })}
                            <Tooltip title="Outra cor">
                                <MDBox
                                    component="label"
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        position: "relative",
                                        overflow: "hidden",
                                        background: corPersonalizada ? atual.cor : "conic-gradient(#e53935, #fdd835, #43a047, #1e88e5, #8e24aa, #e53935)",
                                        outline: corPersonalizada ? `2px solid ${darkMode ? "#FFFFFF" : "#1A73E8"}` : "none",
                                        outlineOffset: "2px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Icon sx={{ color: corPersonalizada ? `${textoSobre(atual.cor)} !important` : "#FFFFFF !important", fontSize: "18px !important" }}>
                                        {corPersonalizada ? "check" : "colorize"}
                                    </Icon>
                                    <input
                                        type="color"
                                        aria-label="Outra cor"
                                        value={atual.cor}
                                        onChange={(e) => alterar("cor", e.target.value)}
                                        style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                                    />
                                </MDBox>
                            </Tooltip>
                        </MDBox>
                        <MDTypography variant="caption" display="block" mt={0.75} sx={{ color: textoSecundario }}>
                            {nomeDaCor(atual.cor)} ({atual.cor}). A tabela dinâmica usa o estilo do Excel mais parecido.
                        </MDTypography>

                        <Grid container spacing={1.5} mt={0.5}>
                            <Grid item xs={8}>
                                <InputLabel sx={labelStyle}>Fonte</InputLabel>
                                <MDSelect
                                    name="fonte"
                                    value={{ value: atual.fonte, label: atual.fonte }}
                                    options={FONTES_PLANILHA.map(f => ({ value: f, label: f }))}
                                    onChange={(o) => alterar("fonte", o?.value)}
                                    formatOptionLabel={(o) => <span style={{ fontFamily: `"${o.value}", sans-serif` }}>{o.label}</span>}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <InputLabel sx={labelStyle}>Tamanho</InputLabel>
                                <MDSelect
                                    name="tamanhoFonte"
                                    value={{ value: atual.tamanhoFonte, label: String(atual.tamanhoFonte) }}
                                    options={TAMANHOS_FONTE.map(t => ({ value: t, label: String(t) }))}
                                    onChange={(o) => alterar("tamanhoFonte", o?.value)}
                                />
                            </Grid>
                        </Grid>

                        <MDBox mt={1.5}>
                            {opcaoSwitch("topo", "Título e filtros no topo", "Nome do relatório, quem gerou e os filtros usados. Desligue para a planilha começar direto nas colunas.")}
                            {opcaoSwitch("totais", "Linha de totais", "Soma dos valores e quantidades no fim; acompanha o filtro do Excel.")}
                            {opcaoSwitch("zebrado", "Linhas zebradas", "Uma linha sim, outra não, com fundo claro na cor escolhida.")}
                        </MDBox>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <InputLabel sx={labelStyle}>Prévia</InputLabel>
                        <MDBox
                            p={2}
                            borderRadius="8px"
                            sx={{ backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", overflowX: "auto", fontFamily: fonteCss, color: "#1A1A1A" }}
                        >
                            {atual.topo && (
                                <MDBox mb={1.5}>
                                    <div style={{ fontWeight: 700, fontSize: px(atual.tamanhoFonte + 3), color: atual.cor }}>Acordos por operador</div>
                                    <div style={{ fontSize: px(Math.max(8, atual.tamanhoFonte - 2)), color: "#595959" }}>Gerado em 03/10/2026 14:32 por você · 4 registros</div>
                                    <div style={{ fontSize: px(Math.max(8, atual.tamanhoFonte - 2)), color: "#595959" }}>Filtros: Período: 01/10/2026 a 03/10/2026</div>
                                </MDBox>
                            )}
                            <table style={{ borderCollapse: "collapse", fontSize: px(atual.tamanhoFonte), width: "100%" }}>
                                <thead>
                                    <tr>
                                        {["Operador", "Data", "Valor"].map(h => (
                                            <th key={h} style={{ ...celula, backgroundColor: atual.cor, color: textoSobre(atual.cor), fontWeight: 700, textAlign: "center" }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {EXEMPLO.map((linha, i) => (
                                        <tr key={linha[0]} style={{ backgroundColor: atual.zebrado && i % 2 === 1 ? clarear(atual.cor, 0.88) : "#FFFFFF" }}>
                                            <td style={celula}>{linha[0]}</td>
                                            <td style={{ ...celula, textAlign: "center" }}>{linha[1]}</td>
                                            <td style={{ ...celula, textAlign: "right" }}>{linha[2]}</td>
                                        </tr>
                                    ))}
                                    {atual.totais && (
                                        <tr style={{ backgroundColor: clarear(atual.cor, 0.92), fontWeight: 700 }}>
                                            <td style={{ ...celula, borderTop: `2px solid ${atual.cor}` }}>Total</td>
                                            <td style={{ ...celula, borderTop: `2px solid ${atual.cor}` }} />
                                            <td style={{ ...celula, borderTop: `2px solid ${atual.cor}`, textAlign: "right" }}>R$ 6.590,50</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </MDBox>
                        <MDTypography variant="caption" display="block" mt={0.75} sx={{ color: textoSecundario }}>
                            A prévia é aproximada: o Excel usa a fonte instalada no computador de quem abrir a planilha.
                        </MDTypography>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1, flexWrap: "wrap" }}>
                <MDButton variant="text" color={darkMode ? "white" : "secondary"} onClick={() => setAtual({ ...ESTILO_PADRAO })} sx={{ mr: "auto" }}>
                    <Icon sx={{ mr: 0.5 }}>restart_alt</Icon> Voltar ao padrão
                </MDButton>
                <MDButton variant="text" color={darkMode ? "white" : "secondary"} onClick={onClose}>Cancelar</MDButton>
                <MDButton variant="gradient" color="info" onClick={() => onSalvar(atual)}>Salvar aparência</MDButton>
            </DialogActions>
        </Dialog>
    );
}

EstiloPlanilhaDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    estilo: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSalvar: PropTypes.func.isRequired,
};
